import * as React from 'react';
import type { SupabaseClient } from '@supabase/supabase-js';
import { getSupabase } from '@/lib/supabase';
import { readPendingInvite, clearPendingInvite } from '@/utils/pendingInvite';
import { resolveAuthPortalRole } from '@/utils/resolveAuthPortalRole';
import { redeemInvite, syncSessionToServer } from '@/data/authApi';

function signInFallbackPath(): string {
  try {
    return localStorage.getItem('coachd:userType') === 'client' ? '/client/auth' : '/coach/sign-in';
  } catch {
    return '/coach/sign-in';
  }
}

function confirmFailedPath(reason: string): string {
  const base = signInFallbackPath();
  const qs = new URLSearchParams({ confirm: 'failed', reason });
  return `${base}?${qs.toString()}`;
}

/**
 * Supabase email-action types we accept on /auth/callback.
 *
 * `token_hash` confirms are the canonical, cross-browser-safe transport
 * (no PKCE localStorage dependency). We only trust the values Supabase
 * actually emits for email actions.
 */
const EMAIL_ACTION_TYPES = new Set(['signup', 'magiclink', 'recovery', 'invite', 'email_change']);

type EmailActionType = 'signup' | 'magiclink' | 'recovery' | 'invite' | 'email_change';

/**
 * SPA callback page for auth redirects.
 *
 * Three modes, checked in order:
 * 1. Email confirm via token_hash (`?token_hash=...&type=signup|...`):
 *    calls `verifyOtp`. Works regardless of which browser opened the email.
 *    This is the path every new signup should take once the Supabase email
 *    template is updated to use {{ .TokenHash }}.
 * 2. PKCE code exchange (`?code=...`): legacy email-link format and OAuth
 *    redirects. Requires the signup browser's localStorage code_verifier.
 * 3. Worker-initiated OAuth: no query params, cookies already set.
 *
 * All three converge on `finishConfirm(sb, data)` which handles session sync,
 * invite redemption, role routing, and the onboarding gate.
 */
export default function AuthCallback() {
  const [status, setStatus] = React.useState('Completing sign-in…');

  React.useEffect(() => {
    (async () => {
      try {
        const url = new URL(window.location.href);
        const code = url.searchParams.get('code');
        const tokenHash = url.searchParams.get('token_hash');
        const rawType = url.searchParams.get('type');
        const emailActionType: EmailActionType | null =
          rawType && EMAIL_ACTION_TYPES.has(rawType) ? (rawType as EmailActionType) : null;
        const inviteFromUrl = url.searchParams.get('invite');
        const tokenFromUrl = url.searchParams.get('token');
        const emailFromUrl = url.searchParams.get('email');
        // TTL-gated read; auto-clears if the stored invite is > 24h old.
        const pending = readPendingInvite();
        const invite = inviteFromUrl || pending.inviteId || undefined;
        const inviteToken = tokenFromUrl || pending.token || undefined;
        const inviteEmail = emailFromUrl || pending.email || undefined;

        const hash = url.hash?.startsWith('#') ? url.hash.slice(1) : url.hash || '';
        const hashParams = new URLSearchParams(hash);
        const authError = url.searchParams.get('error') || hashParams.get('error');
        const authErrorCode = url.searchParams.get('error_code') || hashParams.get('error_code');
        const authErrorDesc = url.searchParams.get('error_description') || hashParams.get('error_description') || '';

        // Supabase sends the user back here with ?error=... when the link is
        // expired/malformed *without* ever producing a code or token_hash.
        if (!code && !tokenHash && (authError || authErrorCode)) {
          const decoded = authErrorDesc
            ? decodeURIComponent(authErrorDesc.replace(/\+/g, ' '))
            : authError || authErrorCode;
          setStatus(
            authErrorCode === 'otp_expired'
              ? 'This confirmation link has expired or was already used.'
              : decoded || 'Email link failed',
          );
          // Dead-end: the link is broken, so the pending invite (if any) is
          // also effectively lost. Clear it rather than letting it pollute
          // the next signup on this browser.
          clearPendingInvite();
          setTimeout(() => {
            window.location.replace(confirmFailedPath(authErrorCode || 'email_link_failed'));
          }, 1500);
          return;
        }

        // Mode 1: token_hash confirm (cross-browser safe)
        if (tokenHash && emailActionType) {
          const sb = getSupabase();
          if (!sb) throw new Error('Auth not configured');
          const { data, error } = await sb.auth.verifyOtp({
            token_hash: tokenHash,
            type: emailActionType,
          });
          if (error) throw error;

          // Password recovery gets its own destination: we don't run the normal
          // role-based routing because the user hasn't picked a new password
          // yet. Just sync the recovery session to Worker cookies so the
          // /reset-password page can call supabase.auth.updateUser, then let
          // that page handle the final redirect.
          if (emailActionType === 'recovery') {
            const session = data.session ?? null;
            if (session) {
              await syncSessionToServer(session);
            }
            // Password reset isn't an invite flow, but the user may have old
            // pending_invite_* keys lingering. Clear them so they can't
            // accidentally fire on the next action.
            clearPendingInvite();

            // Resolve the recovery user's real role and stamp the portal hint
            // in localStorage so /reset-password and the post-reset landing
            // render the correct side. If the user clicked "Forgot password"
            // from one portal but the email belongs to the other portal's
            // role, flag mismatch=1 so ResetPassword can surface a note.
            const recoveryUser = data.user ?? session?.user ?? null;
            let recoveredRole: 'coach' | 'client' | null = null;
            if (recoveryUser) {
              try {
                recoveredRole = await resolveAuthPortalRole(sb, recoveryUser);
              } catch {
                recoveredRole = null;
              }
              if (recoveredRole) {
                try {
                  localStorage.setItem('coachd:userType', recoveredRole);
                } catch {
                  // OBS-11 a-ignore: role hint for the next landing page only; a storage/setter failure just means the default route.
                }
              }
            }

            const startedFrom = url.searchParams.get('portal');
            const mismatch = !!startedFrom && !!recoveredRole && startedFrom !== recoveredRole ? '1' : '';
            const resetQs = new URLSearchParams();
            if (mismatch) resetQs.set('mismatch', '1');
            if (recoveredRole) resetQs.set('role', recoveredRole);
            const resetSearch = resetQs.toString();
            window.location.replace(`/reset-password${resetSearch ? `?${resetSearch}` : ''}`);
            return;
          }

          await finishConfirm(sb, data, { invite, inviteToken, inviteEmail });
          return;
        }

        // Mode 2: PKCE code exchange (OAuth redirects + legacy email links)
        if (code) {
          const sb = getSupabase();
          if (!sb) throw new Error('Auth not configured');
          const { data, error } = await sb.auth.exchangeCodeForSession(code);
          if (error) throw error;
          await finishConfirm(sb, data, { invite, inviteToken, inviteEmail });
          return;
        }

        // Mode 3: Worker-initiated OAuth (cookies already set, no URL params)
        const response = await fetch('/api/auth/session', { credentials: 'include' });
        if (!response.ok) throw new Error(`Session fetch failed: ${response.status}`);

        const json = await response.json();
        const user = json.session?.user;
        if (!user) {
          window.location.replace(signInFallbackPath());
          return;
        }

        if (invite) {
          clearPendingInvite();
          window.location.replace(`/onboarding?invite=${encodeURIComponent(invite)}`);
          return;
        }

        if (inviteToken && inviteEmail) {
          clearPendingInvite();
          window.location.replace(
            `/onboarding?token=${encodeURIComponent(inviteToken)}&email=${encodeURIComponent(inviteEmail)}`,
          );
          return;
        }

        // Non-invite Worker-OAuth landing: clear any stale pending invite so
        // it can't fire on a subsequent unrelated signup in this browser.
        clearPendingInvite();
        window.location.replace('/clients');
      } catch (error: any) {
        console.error('[auth-callback] Error:', error);
        setStatus(`Error: ${error?.message || 'Authentication failed'}`);
        // Failed confirm: the pending invite is effectively dead; clear it
        // rather than letting it leak into the next signup attempt.
        clearPendingInvite();
        const reason = encodeURIComponent(error?.message || 'confirm_failed');
        setTimeout(() => {
          window.location.replace(confirmFailedPath(reason));
        }, 1500);
      }
    })();
  }, []);

  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-6 text-foreground">
      <div className="text-center">
        <div className="mx-auto mb-4 h-8 w-8 animate-spin rounded-full border-b-2 border-primary"></div>
        <p>{status}</p>
      </div>
    </div>
  );
}

/**
 * Shared post-verification finisher.
 *
 * Runs after either `verifyOtp` or `exchangeCodeForSession` has produced a
 * session + user. Handles:
 * 1. Pushing tokens to the Worker cookie jar via /api/auth/session-sync.
 * 2. Invite redemption (client signup path).
 * 3. Resolving the user's role from user_metadata -> app_metadata -> profiles.
 * 4. Routing: clients and already-onboarded coaches go to /; new coaches go
 *    to /coach/onboarding.
 */
async function finishConfirm(
  sb: SupabaseClient,
  data: { session?: any; user?: any },
  inviteCtx: { invite?: string; inviteToken?: string; inviteEmail?: string },
): Promise<void> {
  const session = data.session;
  const sessionUser = session?.user ?? data.user;

  if (session) {
    await syncSessionToServer(session);
  }

  let invite = inviteCtx.invite;
  const { inviteToken, inviteEmail } = inviteCtx;

  if (!invite && inviteToken && inviteEmail) {
    // Best-effort: on failure we fall through to the token+email onboarding URL below.
    const redeemed = await redeemInvite({ token: inviteToken, email: inviteEmail, userId: sessionUser?.id });
    if (redeemed.ok && redeemed.inviteId) {
      invite = String(redeemed.inviteId);
    }
  }

  if (invite) {
    clearPendingInvite();
    window.location.replace(`/onboarding?invite=${encodeURIComponent(invite)}`);
    return;
  }

  if (inviteToken && inviteEmail) {
    clearPendingInvite();
    window.location.replace(
      `/onboarding?token=${encodeURIComponent(inviteToken)}&email=${encodeURIComponent(inviteEmail)}`,
    );
    return;
  }

  // Non-invite confirm (normal signup, social OAuth, etc.): make sure any
  // stale pending_invite_* keys from a prior aborted attempt don't linger.
  clearPendingInvite();

  if (!sessionUser?.id) throw new Error('No user after sign-in');

  let role = (sessionUser.user_metadata as { role?: string })?.role;
  if (role !== 'coach' && role !== 'client') {
    role = (sessionUser.app_metadata as { role?: string })?.role;
  }
  if (role !== 'coach' && role !== 'client') {
    const { data: prof } = await sb.from('profiles').select('role').eq('id', sessionUser.id).maybeSingle();
    if (prof?.role === 'coach' || prof?.role === 'client') {
      role = prof.role;
    }
  }
  if (role !== 'coach' && role !== 'client' && (invite || (inviteToken && inviteEmail))) {
    role = 'client';
  }

  const onboardingComplete = !!(sessionUser.user_metadata as { onboarding_complete?: boolean })?.onboarding_complete;

  // Signal "freshly email-confirmed" to the landing page via a URL query
  // param rather than localStorage. This avoids Safari private-mode storage
  // quota issues and cross-tab pollution, and is self-healing: the landing
  // page strips the param via history.replaceState after firing the toast.
  if (role === 'client') {
    try {
      localStorage.setItem('coachd:userType', 'client');
    } catch {
      // OBS-11 a-ignore: role hint for the next landing page only; a storage/setter failure just means the default route.
    }
    window.location.replace('/?confirmed=1');
    return;
  }

  try {
    localStorage.setItem('coachd:userType', 'coach');
  } catch {
    // OBS-11 a-ignore: role hint for the next landing page only; a storage/setter failure just means the default route.
  }
  window.location.replace(onboardingComplete ? '/?confirmed=1' : '/coach/onboarding?confirmed=1');
}
